<template>
  <div id="cart-view">
    <v-row class="mt-0">
      <v-col cols="12" class="pt-0">
        <v-card class="pa-5">
          <v-data-table
            v-if="cartItems.length"
            :headers="headers"
            :items="cartItems"
            class="elevation-1"
            :items-per-page="5"
            :footer-props="{ 'items-per-page-options': [5, 10, 20, -1] }"
          >
            <template v-slot:item.description="{ item }">
              {{ getDescription(item) }}
            </template>

            <template v-slot:item.quantity="{ item }">
              <div class="d-flex align-center justify-center" style="gap: 4px">
                <v-btn icon small @click="decreaseQuantity(item.id)">
                  <v-icon small>mdi-minus</v-icon>
                </v-btn>

                <v-text-field
                  v-model.number="item.quantity"
                  type="number"
                  hide-details
                  single-line
                  dense
                  class="quantity-input"
                  style="width: 20px"
                  @blur="validateQuantity(item)"
                />

                <v-btn icon small @click="increaseQuantity(item.id)">
                  <v-icon small>mdi-plus</v-icon>
                </v-btn>
              </div>
            </template>

            <template v-slot:item.material="{ item }">
              <v-select
                v-model="item.material"
                :items="itemMaterial || []"
                dense
                hide-details
                variant="underlined"
                style="min-width: 150px"
                @change="val => updateMaterial(item.id, val)"
              />
            </template>

            <template v-slot:item.comment="{ item }">
              <v-text-field
                :value="item.comment"
                @input="val => updateComment(item.id, val)"
                dense
                hide-details
                :placeholder="t('WriteAComment')"
              />
            </template>

            <template #[`item.access`]="{ item }">
              <v-icon
                class="grey-hover-grey mr-2"
                :color="item.accessDocument ? 'primary' : ''"
                @click="toggleAccessDocument(item)"
              >
                {{ mdiIcons.mdiFolder }}
              </v-icon>

              <div class="mr-2" style="display: inline-block">
                <template v-if="isLoadingUpload">
                  <v-progress-circular indeterminate size="20" color="primary"></v-progress-circular>
                </template>
                <template v-else>
                  <v-icon
                    class="grey-hover-grey"
                    :color="item.accessDocument ? 'primary' : ''"
                    @click="goToUpload(item)"
                  >
                    {{ mdiIcons.mdiUpload }}
                  </v-icon>
                </template>
              </div>

              <span v-if="item.accessDocument?.name" class="ml-2">
                {{ getLocalizedTitle(item.accessDocument.name) }}
              </span>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn icon class="trash-btn" @click="removeItem(item.id)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>

            <template v-slot:body.append>
              <tr>
                <td colspan="4"></td>
                <td class="text-center font-weight-bold">
                  <div>
                    {{ t('TOTAL') }}:
                    <span
                      style="
                        color: var(--v-primary-base);
                        font-size: 20px;
                        font-weight: bold;
                        margin-left: 10px;
                        display: inline-block;
                        min-width: 40px;
                        text-align: center;
                      "
                    >
                      {{ totalQuantity }}
                    </span>
                  </div>
                </td>
                <td>
                  <v-btn icon color="grey lighten-2" @click="clearCart" :title="t('CleanCart')">
                    <v-icon dark>mdi-delete-sweep</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
          <v-row class="mt-5" v-if="cartItems.length" justify="start">
            <v-col cols="12" md="4">
              <v-checkbox v-model="incotermDAPModel" label="Incoterm: DAP" color="primary" />
            </v-col>
          </v-row>
          <v-row class="mt-7" v-if="cartItems.length" justify="end">
            <v-col cols="auto">
              <v-btn
                :loading="loadingSave"
                text
                class="d-flex align-center justify-center background-color-1-60 px-5"
                style="height: 50px !important; min-width: 200px"
                @click="savePreOrder"
              >
                <v-icon left color="white" size="28" class="mr-2"> mdi-content-save </v-icon>
                <span class="font-size-14 font-weight-semibold white-color">
                  {{ t('SaveOrder') }}
                </span>
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn
                v-if="canPlaceOrder"
                :loading="loadingPlace"
                text
                class="d-flex align-center justify-center background-color-1-60 px-5"
                style="height: 50px !important; min-width: 200px"
                @click="sendOrder"
              >
                <v-icon left color="white" size="28" class="mr-2"> mdi-cart-arrow-down </v-icon>
                <span class="font-size-14 font-weight-semibold white-color">
                  {{ t('PlaceOrder') }}
                </span>
              </v-btn>
            </v-col>
          </v-row>

          <div v-else class="text-center grey--text">
            <v-icon large class="mb-2">mdi-cart-outline</v-icon>
            <div>{{ t('NoProductsCart') }}</div>
          </div>

          <v-dialog v-model="isAccessDialogVisible" max-width="700">
            <v-card>
              <v-card-title>{{ t('SelectDocument') }}</v-card-title>
              <v-card-text>
                <folder-document-selector
                  :lang="locale"
                  :can-select-folders="false"
                  :can-select-documents="true"
                  :show-documents="true"
                  :multiple="false"
                  :open-selected="false"
                  :show-open-buttons="false"
                  @selection-update="onDocumentSelected"
                />
              </v-card-text>
            </v-card>
          </v-dialog>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { useUtils } from '@/plugins/i18n/utils'
import { EventBus } from '@/plugins/event-bus'
import store from '@/store'
import { ref, computed, onMounted } from 'vue'
import { i18n } from '@/plugins/i18n'
import LibClientManagementRs from 'lib-client-management-rs/src/lib-components/client-management-rs.vue'
import sparePartManagement from 'lib-spare-part-management-vue/src/lib-components/spare-parts/sparePartManagement.js'
import { mdiFolder, mdiUpload } from '@mdi/js'
import FolderDocumentSelector from 'lib-hermes-components/src/lib-components/treeviews/folder-document-selector-with-configuration.vue'
import LibDocumentManagementRs from 'lib-document-management-rs/src/lib-components/document-management-rs.vue'
import '@mdi/font/css/materialdesignicons.css'
import { useRouter } from '@core/utils'
import LibSparePartManagementRs from 'lib-spare-part-management-rs/src/lib-components/spare-part-management-rs.vue'
import Authentication from 'lib-security/src/lib-components/security.vue'
import shoppingCartProperties from 'lib-properties/src/lib-components/modules/shoppingCart'

export default {
  components: {
    FolderDocumentSelector,
  },

  setup() {
    const { createOrder, basePath, lang, loading, watch } = sparePartManagement()

    const { getFolderIdForShoppingCartAttachments, getItemMaterial } = shoppingCartProperties()

    const { t } = useUtils()

    const userData = computed(() => {
      var tokenParsed = {}
      try {
        var tokenParsed = Authentication.methods.getTokenParsed()
        tokenParsed.roles = tokenParsed.realm_access.roles
      } catch (error) {
        console.log(error)
      } finally {
        return tokenParsed
      }
    })

    const canPlaceOrder = computed(() => {
      return userData.value.roles?.includes('hermes-shopping-cart-management')
    })

    const cartItems = computed(() => store.state.app.cart.items || [])

    function addItem(item) {
      store.commit('app/cart/addItem', {
        ...item,
        comment: item.comment || '',
      })
    }

    function validateQuantity(item) {
      const value = parseInt(item.quantity)
      if (isNaN(value) || value < 1) {
        item.quantity = 1
      } else {
        item.quantity = value
      }
      store.commit('app/cart/setItemQuantity', { id: item.id, quantity: item.quantity })
    }

    function increaseQuantity(id) {
      const item = cartItems.value.find(i => i.id === id)
      if (item) {
        store.commit('app/cart/addItem', { ...item, quantity: 1 })
      }
    }

    function decreaseQuantity(id) {
      store.commit('app/cart/decreaseItem', id)
    }

    function removeItem(id) {
      store.commit('app/cart/removeItem', id)
    }

    const totalQuantity = computed(() => cartItems.value.reduce((sum, item) => sum + item.quantity, 0))

    function updateComment(id, comment) {
      store.commit('app/cart/updateComment', { id, comment })
    }

    function updateMaterial(id, material) {
      store.commit('app/cart/updateMaterial', { id, material })
    }

    function clearCart() {
      store.commit('app/cart/clearCart')
    }

    const incotermDAPModel = computed({
      get() {
        return store.state.app.cart.incotermDAP
      },
      set(value) {
        store.dispatch('app/cart/setIncotermDAP', value)
      },
    })

    const locale = computed(() => i18n.locale)

    const headers = computed(() => [
      { text: t('LIB.SPM.Type'), value: 'type' },
      { text: t('LIB.SPM.Description'), value: 'description' },
      { text: t('LIB.SPM.Reference'), value: 'reference' },
      {
        text: t('LIB.CM.Quantity'),
        value: 'quantity',
        sortable: false,
        align: 'center',
      },
      {
        text: 'Material',
        value: 'material',
        sortable: false,
        align: 'center',
      },
      { text: t('LIB.DM.Comment'), value: 'comment', sortable: false },
      { text: t('Document'), value: 'access', align: 'center', sortable: false },
      { text: '', value: 'actions', sortable: false },
    ])

    function getDescription(item) {
      const lang = locale.value

      if (typeof item.description === 'string') {
        return item.description
      }

      if (typeof item.description === 'object' && item.description !== null) {
        return item.description[lang] || Object.values(item.description)[0]
      }
    }

    function getLocalizedTitle(titles) {
      const lang = locale.value

      if (Array.isArray(titles)) {
        const localized = titles.find(t => t.locale === lang)
        if (localized) return localized.title

        if (titles.length > 0) return titles[0].title
      }

      return ''
    }

    function getPageTitle() {
      const pageTitle = i18n.t('ProcessCart')
      EventBus.$emit('update-page-header-title', pageTitle)
    }

    function getPathBreadcrumb() {
      var array = []
      array.push({
        text: i18n.t('ProcessCart'),
      })
      EventBus.$emit('update-path-breadcrumb-path', basePath.value.concat(array))
    }

    const realm = computed(() => {
      var id = window.location.hostname
      if (id === 'localhost') {
        id = 'gemsys-dev.geminys.com'
      }
      return id
    })

    watch(
      [lang, loading],
      () => {
        getPageTitle()
        getPathBreadcrumb()
      },
      {
        deep: true,
      },
    )

    async function getClient() {
      try {
        var client = await LibClientManagementRs.methods.getClient(null, realm.value)
        return client.data
      } catch (error) {
        console.log(error)
        return null
      }
    }

    const isAccessDialogVisible = ref(false)
    const selectedItemId = ref(null)
    const selectedDocument = ref(null)

    async function onDocumentSelected(doc) {
      if (!doc || !selectedItemId.value) return

      const fullDoc = await getDocumentFromId(doc)
      if (!fullDoc) return

      const documentData = {
        id: fullDoc.objectId?.id || null,
        name: fullDoc.titles,
      }

      store.commit('app/cart/updateItemAccessDocument', {
        id: selectedItemId.value,
        document: documentData,
      })

      selectedDocument.value = documentData
      isAccessDialogVisible.value = false
      selectedItemId.value = null
    }

    const documentData = ref(null)
    const root = ref(null)
    const pool = ref(null)
    const annexPool = ref(null)

    async function getDocumentFromId(id) {
      try {
        if (id != root.value && id != pool.value && id != annexPool.value) {
          const response = await LibDocumentManagementRs.methods.getDocumentData(id, false)
          documentData.value = response.data
        }
        return documentData.value
      } catch (error) {
        console.error('Error getting document:', error)
        return null
      }
    }

    const loadingSave = ref(false)
    const loadingPlace = ref(false)

    async function sendOrder() {
      loadingPlace.value = true
      const client = await getClient()

      const formattedItems = cartItems.value.map(item => {
        let description

        if (typeof item.description === 'object') {
          description = item.description
        } else {
          description = { text: item.description }
        }

        return {
          reference: item.reference,
          description,
          type: item.type,
          quantity: item.quantity,
          comment: item.comment || null,
          accessDocument: item.accessDocument?.id || null,
          material: item.material || null,
        }
      })

      const orderPayload = {
        createdBy: client.name,
        createdAt: new Date().toISOString(),
        state: 'Sent',
        incotermDAP: store.state.app.cart.incotermDAP,
        items: formattedItems,
      }

      let success = false
      try {
        const editingOrderNo = store.state.app.cart.editingOrderNo

        let result
        if (editingOrderNo) {
          orderPayload.orderNo = editingOrderNo
          result = await LibSparePartManagementRs.methods.editOrder(orderPayload, true)
        } else {
          result = await createOrder(orderPayload)
        }

        success = !!result
        if (success) clearCart()
      } catch (error) {
        console.error('Error sending order:', error)
      } finally {
        loadingPlace.value = false
        EventBus.$emit('update-notification-data-default', {
          visible: true,
          timeout: 3000,
          prependIcon: success ? 'done' : 'error',
          type: success ? 'success' : 'error',
          title: success ? i18n.t('LIB.SPM.SendSuccess') : i18n.t('LIB.SPM.SendError'),
        })
      }
    }

    async function savePreOrder() {
      loadingSave.value = true
      const client = await getClient()

      const formattedItems = cartItems.value.map(item => {
        let description

        if (typeof item.description === 'object') {
          description = item.description
        } else {
          description = { text: item.description }
        }

        return {
          reference: item.reference,
          description,
          type: item.type,
          quantity: item.quantity,
          comment: item.comment || null,
          accessDocument: item.accessDocument?.id || null,
          material: item.material || null,
        }
      })

      const orderPayload = {
        createdBy: client.name,
        createdAt: new Date().toISOString(),
        state: 'Waiting to send',
        incotermDAP: store.state.app.cart.incotermDAP,
        items: formattedItems,
      }

      let success = false
      try {
        const editingOrderNo = store.state.app.cart.editingOrderNo

        let result
        if (editingOrderNo) {
          orderPayload.orderNo = editingOrderNo
          result = await LibSparePartManagementRs.methods.editOrder(orderPayload, true)
        } else {
          result = await createOrder(orderPayload)
        }

        success = !!result
        if (success) clearCart()
      } catch (error) {
        console.error('Error saving order:', error)
      } finally {
        loadingSave.value = false
        EventBus.$emit('update-notification-data-default', {
          visible: true,
          timeout: 3000,
          prependIcon: 'save',
          appendIcon: success ? 'done' : 'error',
          type: success ? 'success' : 'error',
          title: success ? i18n.t('LIB.SPM.PreorderSuccess') : i18n.t('LIB.SPM.PreorderError'),
        })
      }
    }

    function toggleAccessDocument(item) {
      if (item.accessDocument) {
        store.commit('app/cart/updateItemAccessDocument', {
          id: item.id,
          document: null,
        })
      } else {
        selectedItemId.value = item.id
        selectedDocument.value = null
        isAccessDialogVisible.value = true
      }
    }

    const folderIdForShoppingCartAttachments = ref(null)
    const itemMaterial = ref([])

    const isLoadingUpload = ref(false)

    async function init() {
      try {
        isLoadingUpload.value = true
        getPageTitle()
        getPathBreadcrumb()

        folderIdForShoppingCartAttachments.value = await getFolderIdForShoppingCartAttachments()
        itemMaterial.value = await getItemMaterial()
      } catch (error) {
        console.error(error)
      } finally {
        isLoadingUpload.value = false
      }
    }

    init()

    const { router, route } = useRouter()

    function goToUpload(item) {
      router.push({
        name: 'upload-document-view',
        params: { id: folderIdForShoppingCartAttachments.value },
        query: { fromCart: 'true', cartItemId: item.id },
      })
    }

    const uploadedDocId = route.value?.query?.uploadedDocId || null
    const uploadedDocNameRaw = route.value?.query?.uploadedDocName || null
    const cartItemId = route.value?.query?.cartItemId || null

    const uploadedDocName = uploadedDocNameRaw ? JSON.parse(uploadedDocNameRaw) : null

    onMounted(() => {
      if (cartItemId && uploadedDocId && uploadedDocName) {
        const documentData = {
          id: uploadedDocId,
          name: uploadedDocName,
        }

        store.commit('app/cart/updateItemAccessDocument', {
          id: cartItemId,
          document: documentData,
        })
      }
    })

    return {
      t,
      cartItems,
      headers,
      removeItem,
      validateQuantity,
      increaseQuantity,
      decreaseQuantity,
      totalQuantity,
      updateComment,
      updateMaterial,
      clearCart,
      sendOrder,
      i18n,
      locale,
      getDescription,
      savePreOrder,
      mdiIcons: {
        mdiFolder,
        mdiUpload,
      },
      isAccessDialogVisible,
      selectedItemId,
      selectedDocument,
      onDocumentSelected,
      getDocumentFromId,
      toggleAccessDocument,
      loadingSave,
      loadingPlace,
      incotermDAPModel,
      itemMaterial,
      goToUpload,
      isLoadingUpload,
      getLocalizedTitle,
      canPlaceOrder,
    }
  },
}
</script>

<style scoped>
/* No arrows on text*/

::v-deep .quantity-input input[type='number']::-webkit-outer-spin-button,
::v-deep .quantity-input input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
::v-deep .quantity-input input[type='number'] {
  -moz-appearance: textfield;
}

/* Ajuste de tamaño del text-field para que no se estire */
.quantity-input {
  width: 56px !important;
  max-width: 56px !important;
}
::v-deep .quantity-input.v-text-field {
  min-width: 0 !important;
}
::v-deep .quantity-input .v-input__slot {
  min-height: 28px;
  padding-left: 0;
  padding-right: 0;
}

/* CENTRAR el texto del input */
::v-deep .quantity-input .v-input__slot input {
  text-align: center;
  padding-left: 0;
  padding-right: 0;
}

/* que la línea de abajo no llegue al borde */
::v-deep .quantity-input .v-input__slot::before,
::v-deep .quantity-input .v-input__slot::after {
  left: 0;
  right: 0;
  margin: 0 auto;
}
</style>
